import React, { Component, ReactNode, ErrorInfo } from 'react';
import Button from '@material-ui/core/Button';
import hardReload from '../../util/hardReload';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { ERROR_STATE_TITLE, ERROR_STATE_HINT } from './errorCopy';

// 'reset' first re-renders the crashed subtree in place, preserving surrounding
// state (unsaved forms) and working offline; if the crash recurs right away it
// escalates, and the next attempt performs a full cache-bypassing page reload.
// 'reload' skips straight to the page reload — right for the root boundary,
// where the whole tree is gone, nothing is left to preserve, and only a reload
// can recover systemic failures (e.g. a stale bundle).
type RecoveryMode = 'reset' | 'reload';

interface Props {
  children: ReactNode;
  recovery?: RecoveryMode;
  // The boundary is mounted outside the theme providers (e.g. at the app
  // root), where the global body background is dark — render the fallback on
  // its own full-page light canvas.
  fullPage?: boolean;
}

// A re-crash within this window of a reset attempt means resetting cannot fix
// the problem — the next attempt escalates to a full page reload.
const ESCALATION_WINDOW_MS = 10_000;

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  escalated: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  private lastResetAt: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, escalated: false };
  }

  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true, error: null, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error,
      errorInfo,
      escalated: this.lastResetAt != null && Date.now() - this.lastResetAt < ESCALATION_WINDOW_MS,
    });

    // Log to console with full details
    console.group('🔴 ErrorBoundary caught an error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo?.componentStack);
    console.groupEnd();

    // TODO: Send to error reporting service (e.g., Sentry)
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    // }
  }

  handleRecover = () => {
    if (this.props.recovery === 'reload' || this.state.escalated) {
      void hardReload();
      return;
    }

    this.lastResetAt = Date.now();
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, escalated } = this.state;
      const isDevelopment = (import.meta as any).env?.MODE === 'development';

      // Nested mounts sit inside existing light surfaces and must not claim
      // viewport height — the full-page canvas is opt-in via `fullPage`.
      const containerStyle = this.props.fullPage
        ? { backgroundColor: '#fff', minHeight: '100vh', boxSizing: 'border-box' as const }
        : undefined;

      return (
        <Box p={3} style={containerStyle}>
          <Alert
            severity="error"
            style={{
              marginBottom: '1.5rem',
              backgroundColor: '#fdeded',
              border: '1px solid #f5c6cb',
            }}
          >
            <AlertTitle style={{ fontWeight: 600, fontSize: '1.1rem' }}>{ERROR_STATE_TITLE}</AlertTitle>
            <Typography variant="body2" style={{ marginTop: '0.5rem' }}>
              {escalated ? 'Still not working — trying again will reload the page.' : ERROR_STATE_HINT}
            </Typography>
            {isDevelopment && error?.message ? (
              <Typography
                variant="body2"
                component="div"
                style={{
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  marginTop: '0.75rem',
                  padding: '0.5rem',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  fontSize: '0.875rem',
                  color: '#d32f2f',
                }}
              >
                {error.message}
              </Typography>
            ) : null}
          </Alert>

          <Box display="flex" mb={3} style={{ gap: '1rem' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleRecover}
              style={{ textTransform: 'none', fontWeight: 500 }}
            >
              Try again
            </Button>
          </Box>

          {isDevelopment && errorInfo?.componentStack && (
            <Paper
              elevation={2}
              style={{
                padding: '1rem',
                backgroundColor: '#fafafa',
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                style={{
                  fontWeight: 600,
                  color: '#666',
                  marginBottom: '0.75rem',
                }}
              >
                🔍 Component Stack (Development Only)
              </Typography>
              <pre
                style={{
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#424242',
                  lineHeight: 1.5,
                }}
              >
                {errorInfo.componentStack}
              </pre>
            </Paper>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}
